import { useEffect, useState } from "react";
import api, { fmtMXN, fmtMXNFull, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, TrendingUp, FileText, FileDown, X, Eye, Zap } from "lucide-react";
import { Modal, In, Sel } from "./Projects";
import { toast } from "sonner";

const ACCESS_LEVELS = [
  { value: "sin_acceso", label: "Sin acceso al sistema" },
  { value: "solo_lectura", label: "Solo lectura (futuro)" },
  { value: "operativo", label: "Operativo (futuro)" },
];

const MOV_TYPES = [
  { value: "aportacion", label: "Aportación", color: "var(--green, #16a34a)" },
  { value: "distribucion", label: "Distribución", color: "var(--magenta, #d946ef)" },
  { value: "interes", label: "Interés / Rendimiento", color: "var(--amber, #d97706)" },
];

const PAYMENT_METHODS = [
  { value: "efectivo", label: "Efectivo" },
  { value: "transferencia", label: "Transferencia" },
  { value: "deposito", label: "Depósito" },
  { value: "cheque", label: "Cheque" },
  { value: "tarjeta_credito", label: "Tarjeta crédito" },
  { value: "tarjeta_debito", label: "Tarjeta débito" },
  { value: "otro", label: "Otro" },
];

const emptyInvestor = {
  name: "", rfc: "", email: "", phone: "", address: "",
  bank_name: "", bank_account: "", clabe: "",
  monthly_yield_pct: 0, access_level: "sin_acceso", notes: "", active: true,
  // Solo para creación inicial — se convierten en movimiento de aportación
  initial_capital: "",
  initial_capital_date: new Date().toISOString().slice(0, 10),
  initial_capital_method: "transferencia",
  initial_capital_reference: "",
};

const emptyMovement = {
  movement_type: "aportacion",
  amount: 0,
  movement_date: new Date().toISOString().slice(0, 10),
  project_id: "",
  payment_method: "transferencia",
  reference: "",
  description: "",
  interest_rate_pct: "",
};

export default function Investors() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInvestor);
  const [detail, setDetail] = useState(null); // { investor + movements + balance }
  const [movOpen, setMovOpen] = useState(false);
  const [movForm, setMovForm] = useState(emptyMovement);
  const canManage = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    const [r, p] = await Promise.all([api.get("/investors"), api.get("/projects")]);
    setRows(r.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const totals = rows.reduce(
    (acc, r) => {
      const b = r.balance || {};
      acc.saldo_capital += b.saldo_capital || b.saldo || 0;
      acc.devengado += b.interes_devengado || 0;
      acc.pagado += b.interes_pagado || b.interes || 0;
      acc.pendiente += b.interes_pendiente || 0;
      acc.mensual += b.rendimiento_mensual_estimado || 0;
      return acc;
    },
    { saldo_capital: 0, devengado: 0, pagado: 0, pendiente: 0, mensual: 0 },
  );

  const startNew = () => { setEditing(null); setForm(emptyInvestor); setOpen(true); };
  const startEdit = (inv) => { setEditing(inv.id); setForm({ ...emptyInvestor, ...inv }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const {
      initial_capital, initial_capital_date, initial_capital_method, initial_capital_reference,
      ...rest
    } = form;
    const payload = { ...rest, monthly_yield_pct: Number(form.monthly_yield_pct || 0) };
    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
    try {
      let investorId = editing;
      if (editing) {
        await api.patch(`/investors/${editing}`, payload);
      } else {
        const { data } = await api.post("/investors", payload);
        investorId = data.id;
        const initAmt = Number(initial_capital || 0);
        if (initAmt > 0 && investorId) {
          // Crea el movimiento de aportación inicial
          const movPayload = {
            investor_id: investorId,
            movement_type: "aportacion",
            amount: initAmt,
            movement_date: initial_capital_date || new Date().toISOString().slice(0, 10),
            payment_method: initial_capital_method || "transferencia",
          };
          if (initial_capital_reference) movPayload.reference = initial_capital_reference;
          try {
            await api.post(`/investors/${investorId}/movements`, movPayload);
          } catch (err) {
            toast.warning(`Inversionista creado, pero no se pudo registrar el capital inicial: ${err.response?.data?.detail || "error"}`);
          }
        }
      }
      setOpen(false);
      load();
      toast.success(editing ? "Inversionista actualizado" : "Inversionista creado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar inversionista? Si tiene movimientos se desactivará en su lugar.")) return;
    try {
      const { data } = await api.delete(`/investors/${id}`);
      load();
      if (data.soft_deleted) {
        toast.warning(`Tiene ${data.movement_count} movimientos: se desactivó (no se borró).`);
      } else {
        toast.success("Eliminado");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/investors/${id}`);
      setDetail(data);
    } catch {
      toast.error("Error al cargar detalle");
    }
  };

  const openNewMovement = () => { setMovForm(emptyMovement); setMovOpen(true); };

  const saveMovement = async (e) => {
    e.preventDefault();
    if (!detail) return;
    const payload = {
      ...movForm,
      investor_id: detail.id,
      amount: Number(movForm.amount || 0),
      interest_rate_pct: movForm.interest_rate_pct === "" ? null : Number(movForm.interest_rate_pct),
    };
    Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] === null) && delete payload[k]);
    try {
      await api.post(`/investors/${detail.id}/movements`, payload);
      const { data } = await api.get(`/investors/${detail.id}`);
      setDetail(data);
      setMovOpen(false);
      load();
      toast.success("Movimiento agregado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const removeMovement = async (movId) => {
    if (!window.confirm("¿Eliminar movimiento?")) return;
    try {
      await api.delete(`/investors/movements/${movId}`);
      const { data } = await api.get(`/investors/${detail.id}`);
      setDetail(data);
      load();
      toast.success("Eliminado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const downloadPDF = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar PDF");
    }
  };

  const liquidateMonthly = async () => {
    if (!detail) return;
    const b = detail.balance || {};
    const capital = b.saldo_capital || b.saldo || 0;
    const rate = detail.monthly_yield_pct || 0;
    const expected = capital * (rate / 100);
    if (rate <= 0) {
      toast.error("Este inversionista no tiene tasa de rendimiento mensual configurada");
      return;
    }
    if (capital <= 0) {
      toast.error("No hay capital vigente para liquidar");
      return;
    }
    const ok = window.confirm(
      `¿Liquidar rendimiento del mes?\n\n` +
      `Capital vigente: ${fmtMXN(capital)}\n` +
      `Tasa: ${rate}% mensual\n` +
      `Monto a registrar: ${fmtMXN(expected)}\n\n` +
      `Se creará el movimiento y se descargará el recibo PDF.`,
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/investors/${detail.id}/liquidate-monthly`);
      const updated = await api.get(`/investors/${detail.id}`);
      setDetail(updated.data);
      load();
      toast.success(`Liquidación de ${data.month_label} creada (${fmtMXN(data.movement.amount)})`);
      // Auto-download receipt PDF (best-effort, no bloquea el flujo si falla)
      try {
        await downloadPDF(
          `/investors/movements/${data.movement.id}/receipt/pdf`,
          `recibo-interes-${detail.name.replace(/ /g, "_")}-${data.movement.movement_date}.pdf`,
        );
      } catch { /* ignore — user can re-download from the movement row */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al liquidar");
    }
  };

  return (
    <div data-testid="investors-page">
      <PageHeader
        subtitle="Capital, distribuciones e intereses"
        title="Inversionistas"
        actions={
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="investors-consolidated-pdf-btn"
              onClick={() => downloadPDF("/investors/consolidated/pdf", `inversionistas-consolidado-${new Date().toISOString().slice(0, 10)}.pdf`)}
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
              style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}
            >
              <FileDown className="w-4 h-4" /> PDF Consolidado
            </button>
            {canManage && (
              <button data-testid="new-investor-btn" onClick={startNew} className="btn-primary px-4 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
                <Plus className="w-4 h-4" /> Nuevo inversionista
              </button>
            )}
          </div>
        }
      />

      <div className="px-4 lg:px-8 py-6 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Capital vigente" value={totals.saldo_capital} color="var(--cyan)" />
          <Kpi label="Devengado a la fecha" value={totals.devengado} color="var(--amber, #d97706)" />
          <Kpi label="Intereses pagados" value={totals.pagado} color="var(--green, #16a34a)" />
          <Kpi label="Pendiente de pago" value={totals.pendiente} color="var(--red, #dc2626)" />
          <Kpi label="Rend. mensual estimado" value={totals.mensual} color="#7c3aed" />
        </div>

        {/* Table */}
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]" data-testid="investors-table">
            <thead className="panel-header">
              <tr className="text-left">
                <Th>Nombre</Th>
                <Th>RFC</Th>
                <Th right>Rend. mes</Th>
                <Th right>Capital</Th>
                <Th right>Devengado</Th>
                <Th right>Pagado</Th>
                <Th right>Pendiente</Th>
                <Th>Estado</Th>
                <Th right>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin inversionistas registrados.</td></tr>
              )}
              {rows.map((inv) => {
                const b = inv.balance || {};
                return (
                  <tr key={inv.id} data-testid={`investor-row-${inv.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                    <Td>
                      <button onClick={() => openDetail(inv.id)} className="text-left font-semibold text-slate-100 hover:underline" style={{ color: "var(--cyan)" }}>
                        {inv.name}
                      </button>
                      {inv.email && <div className="text-xs text-slate-500">{inv.email}</div>}
                    </Td>
                    <Td className="font-mono text-xs">{inv.rfc || "—"}</Td>
                    <Td right className="font-mono" style={{ color: "#7c3aed" }}>{(inv.monthly_yield_pct || 0).toFixed(2)}%</Td>
                    <Td right className="font-mono" style={{ color: "var(--cyan)" }}>{fmtMXN(b.saldo_capital || b.saldo || 0)}</Td>
                    <Td right className="font-mono" style={{ color: "var(--amber, #d97706)" }}>{fmtMXN(b.interes_devengado || 0)}</Td>
                    <Td right className="font-mono" style={{ color: "var(--green, #16a34a)" }}>{fmtMXN(b.interes_pagado || b.interes || 0)}</Td>
                    <Td right className="font-mono font-bold" style={{ color: "var(--red, #dc2626)" }}>{fmtMXN(b.interes_pendiente || 0)}</Td>
                    <Td>
                      {inv.active ? (
                        <span className="label-mono text-[10px] px-2 py-0.5 rounded-sm" style={{ background: "rgba(34,197,94,0.12)", color: "var(--green, #16a34a)" }}>ACTIVO</span>
                      ) : (
                        <span className="label-mono text-[10px] px-2 py-0.5 rounded-sm" style={{ background: "rgba(148,163,184,0.15)", color: "#94a3b8" }}>INACTIVO</span>
                      )}
                    </Td>
                    <Td right>
                      <div className="flex justify-end gap-1">
                        <button data-testid={`view-${inv.id}`} onClick={() => openDetail(inv.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }} title="Ver detalle"><Eye className="w-4 h-4" /></button>
                        {canManage && (
                          <>
                            <button onClick={() => startEdit(inv)} className="p-1 rounded-sm hover:bg-white/5 text-slate-300" title="Editar"><FileText className="w-4 h-4" /></button>
                            <button data-testid={`delete-${inv.id}`} onClick={() => remove(inv.id)} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--red, #dc2626)" }} title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Investor form modal */}
      {open && (
        <Modal
          onClose={() => setOpen(false)}
          title={editing ? "Editar inversionista" : "Nuevo inversionista"}
          footer={
            <>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" form="investor-form" data-testid="save-investor-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </>
          }
        >
          <form id="investor-form" onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <In label="Nombre / Razón social" testId="inv-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <In label="RFC" testId="inv-rfc" value={form.rfc} onChange={(v) => setForm({ ...form, rfc: v })} required={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Email" testId="inv-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required={false} />
              <In label="Teléfono" testId="inv-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required={false} />
            </div>
            <In label="Dirección" testId="inv-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} required={false} />

            <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)" }}>
              <div className="label-mono" style={{ color: "var(--cyan)" }}>// DATOS BANCARIOS</div>
              <div className="grid grid-cols-2 gap-3">
                <In label="Banco" testId="inv-bank" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} required={false} />
                <In label="N° de cuenta" testId="inv-acct" value={form.bank_account} onChange={(v) => setForm({ ...form, bank_account: v })} required={false} />
              </div>
              <In label="CLABE interbancaria" testId="inv-clabe" value={form.clabe} onChange={(v) => setForm({ ...form, clabe: v })} required={false} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <In label="% Rendimiento mensual (ej. 4 = 4% mensual)" testId="inv-yield" type="number" value={form.monthly_yield_pct} onChange={(v) => setForm({ ...form, monthly_yield_pct: v })} required={false} />
              <Sel label="Nivel de acceso" testId="inv-access" value={form.access_level} onChange={(v) => setForm({ ...form, access_level: v })} options={ACCESS_LEVELS} />
            </div>

            {!editing && (
              <div className="panel p-3 space-y-3" style={{ background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.3)" }}>
                <div className="label-mono" style={{ color: "#a78bfa" }}>// CAPITAL INICIAL APORTADO (OPCIONAL)</div>
                <div className="text-[11px] text-slate-400 -mt-2">Si llenas el monto, se registrará automáticamente como primera aportación del inversionista.</div>
                <div className="grid grid-cols-2 gap-3">
                  <In label="Monto MXN" testId="inv-initial-capital" type="number" value={form.initial_capital} onChange={(v) => setForm({ ...form, initial_capital: v })} required={false} />
                  <In label="Fecha de aportación" testId="inv-initial-date" type="date" value={form.initial_capital_date} onChange={(v) => setForm({ ...form, initial_capital_date: v })} required={false} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Sel label="Método de pago" testId="inv-initial-method" value={form.initial_capital_method} onChange={(v) => setForm({ ...form, initial_capital_method: v })} options={PAYMENT_METHODS} />
                  <In label="Referencia / Folio" testId="inv-initial-ref" value={form.initial_capital_reference} onChange={(v) => setForm({ ...form, initial_capital_reference: v })} required={false} />
                </div>
              </div>
            )}
            <In label="Notas" testId="inv-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} required={false} />

            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="inv-active" />
              Inversionista activo
            </label>
          </form>
        </Modal>
      )}

      {/* Detail slide-over */}
      {detail && (
        <DetailPanel
          detail={detail}
          projects={projects}
          canManage={canManage}
          onClose={() => setDetail(null)}
          onAddMovement={openNewMovement}
          onRemoveMovement={removeMovement}
          onDownloadPDF={() => downloadPDF(`/investors/${detail.id}/statement/pdf`, `estado-${detail.name.replace(/ /g, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`)}
          onDownloadReceipt={(m) => downloadPDF(`/investors/movements/${m.id}/receipt/pdf`, `recibo-interes-${detail.name.replace(/ /g, "_")}-${m.movement_date || "now"}.pdf`)}
          onLiquidateMonthly={liquidateMonthly}
        />
      )}

      {/* Movement modal */}
      {movOpen && detail && (
        <Modal
          onClose={() => setMovOpen(false)}
          title={`Nuevo movimiento · ${detail.name}`}
          footer={
            <>
              <button type="button" onClick={() => setMovOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" form="movement-form" data-testid="save-movement-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </>
          }
        >
          <form id="movement-form" onSubmit={saveMovement} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Tipo de movimiento" testId="mov-type" value={movForm.movement_type} onChange={(v) => setMovForm({ ...movForm, movement_type: v })} options={MOV_TYPES} />
              <In label="Fecha" testId="mov-date" type="date" value={movForm.movement_date} onChange={(v) => setMovForm({ ...movForm, movement_date: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Monto MXN" testId="mov-amount" type="number" value={movForm.amount} onChange={(v) => setMovForm({ ...movForm, amount: v })} />
              <Sel label="Método de pago" testId="mov-payment" value={movForm.payment_method} onChange={(v) => setMovForm({ ...movForm, payment_method: v })} options={PAYMENT_METHODS} />
            </div>
            <Sel
              label="Vincular a obra (opcional)"
              testId="mov-project"
              value={movForm.project_id}
              onChange={(v) => setMovForm({ ...movForm, project_id: v })}
              options={[{ value: "", label: "Sin obra específica" }, ...projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))]}
            />
            <div className="grid grid-cols-2 gap-3">
              <In label="Referencia / Folio" testId="mov-ref" value={movForm.reference} onChange={(v) => setMovForm({ ...movForm, reference: v })} required={false} />
              {movForm.movement_type === "interes" && (
                <In label="Tasa de interés %" testId="mov-rate" type="number" value={movForm.interest_rate_pct} onChange={(v) => setMovForm({ ...movForm, interest_rate_pct: v })} required={false} />
              )}
            </div>
            <In label="Descripción" testId="mov-desc" value={movForm.description} onChange={(v) => setMovForm({ ...movForm, description: v })} required={false} />
          </form>
        </Modal>
      )}
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>
);

const Kpi = ({ label, value, color }) => (
  <div className="panel p-3">
    <div className="label-mono text-[10px] mb-1" style={{ color }}>{label}</div>
    <div className="font-mono text-lg font-bold text-slate-100">{fmtMXNFull(value || 0)}</div>
  </div>
);

function DetailPanel({ detail, projects, canManage, onClose, onAddMovement, onRemoveMovement, onDownloadPDF, onDownloadReceipt, onLiquidateMonthly }) {
  const b = detail.balance || {};
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="investor-detail-panel">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl panel-strong overflow-y-auto" style={{ background: "#0a0f1d" }}>
        <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="label-mono" style={{ color: "var(--cyan)" }}>// INVERSIONISTA</div>
            <h2 className="font-display font-bold text-xl text-slate-100">{detail.name}</h2>
            <div className="text-[11px] text-slate-500 font-mono">
              {detail.rfc ? `RFC ${detail.rfc} · ` : ""}{(detail.monthly_yield_pct || 0)}% rendimiento mensual
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Balance */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <MiniKpi label="Capital vigente" value={b.saldo_capital || b.saldo} color="var(--cyan)" big />
            <MiniKpi label="Devengado" value={b.interes_devengado} color="var(--amber, #d97706)" />
            <MiniKpi label="Pagado" value={b.interes_pagado || b.interes} color="var(--green, #16a34a)" />
            <MiniKpi label="Pendiente" value={b.interes_pendiente} color="var(--red, #dc2626)" />
          </div>

          {(b.monthly_yield_pct > 0 || detail.monthly_yield_pct > 0) && (
            <div className="panel p-3 text-xs" style={{ background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.3)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-mono mb-0.5" style={{ color: "#a78bfa" }}>// RENDIMIENTO ESTIMADO MENSUAL</div>
                  <div className="text-[11px] text-slate-400">
                    {(b.monthly_yield_pct || detail.monthly_yield_pct || 0)}% mensual sobre {fmtMXN(b.saldo_capital || b.saldo || 0)} de capital vigente
                  </div>
                </div>
                <div className="font-mono font-bold text-lg" style={{ color: "#a78bfa" }}>
                  {fmtMXN(b.rendimiento_mensual_estimado || 0)}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="investor-statement-pdf-btn"
              onClick={onDownloadPDF}
              className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
              style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}
            >
              <FileDown className="w-4 h-4" /> Estado de cuenta PDF
            </button>
            {canManage && (
              <button
                data-testid="add-movement-btn"
                onClick={onAddMovement}
                className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
              >
                <Plus className="w-4 h-4" /> Movimiento
              </button>
            )}
            {canManage && (detail.monthly_yield_pct || 0) > 0 && (
              <button
                data-testid="liquidate-monthly-btn"
                onClick={onLiquidateMonthly}
                className="btn-primary px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
                style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
                title="Calcular y registrar el rendimiento del mes corriente + descargar recibo"
              >
                <Zap className="w-4 h-4" /> Liquidar mes
              </button>
            )}
          </div>

          {/* Banco */}
          {(detail.bank_name || detail.clabe || detail.bank_account) && (
            <div className="panel p-3 text-xs space-y-1" style={{ background: "rgba(8,12,24,0.6)" }}>
              <div className="label-mono" style={{ color: "var(--cyan)" }}>// DATOS BANCARIOS</div>
              {detail.bank_name && <div>Banco: <span className="text-slate-100">{detail.bank_name}</span></div>}
              {detail.bank_account && <div>Cuenta: <span className="font-mono text-slate-100">{detail.bank_account}</span></div>}
              {detail.clabe && <div>CLABE: <span className="font-mono text-slate-100">{detail.clabe}</span></div>}
            </div>
          )}

          {/* Movements */}
          <div>
            <div className="label-mono mb-2" style={{ color: "var(--cyan)" }}>// HISTORIAL DE MOVIMIENTOS ({detail.movements?.length || 0})</div>
            {(!detail.movements || detail.movements.length === 0) ? (
              <div className="text-center py-8 text-slate-500 text-xs">Sin movimientos registrados.</div>
            ) : (
              <div className="space-y-2">
                {detail.movements.map((m) => {
                  const type = MOV_TYPES.find((t) => t.value === m.movement_type);
                  const sign = m.movement_type === "aportacion" ? "+" : "−";
                  return (
                    <div key={m.id} className="panel p-3 flex items-start gap-3" style={{ background: "rgba(8,12,24,0.4)" }}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="label-mono text-[10px] px-2 py-0.5 rounded-sm" style={{ background: "rgba(255,255,255,0.04)", color: type?.color }}>
                            {type?.label || m.movement_type}
                          </span>
                          <span className="text-xs text-slate-400">{fmtDate(m.movement_date)}</span>
                          {m.project_code && <span className="text-[10px] text-slate-500 font-mono">{m.project_code}</span>}
                          {m.reference && <span className="text-[10px] text-slate-500">Ref: {m.reference}</span>}
                          {m.interest_rate_pct != null && <span className="text-[10px]" style={{ color: "var(--amber, #d97706)" }}>{m.interest_rate_pct}%</span>}
                        </div>
                        {m.description && <div className="text-xs text-slate-400 mt-1">{m.description}</div>}
                        <div className="text-[10px] text-slate-500 mt-1">
                          {PAYMENT_METHODS.find((p) => p.value === m.payment_method)?.label || m.payment_method}
                          {m.created_by_name && ` · capturado por ${m.created_by_name}`}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-lg" style={{ color: type?.color }}>
                          {sign}{fmtMXN(m.amount)}
                        </div>
                        <div className="flex flex-col items-end gap-0.5 mt-1">
                          {m.movement_type === "interes" && (
                            <button
                              data-testid={`receipt-pdf-${m.id}`}
                              onClick={() => onDownloadReceipt(m)}
                              className="text-[10px] hover:underline flex items-center gap-1"
                              style={{ color: "var(--amber, #d97706)" }}
                              title="Generar recibo de pago de intereses"
                            >
                              <FileDown className="w-3 h-3" /> Recibo PDF
                            </button>
                          )}
                          {canManage && (
                            <button onClick={() => onRemoveMovement(m.id)} className="text-[10px] text-slate-500 hover:text-red-400">
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const MiniKpi = ({ label, value, color, big }) => (
  <div className="panel p-2">
    <div className="label-mono text-[9px]" style={{ color }}>{label}</div>
    <div className={`font-mono font-bold text-slate-100 ${big ? "text-lg" : "text-sm"}`}>{fmtMXN(value || 0)}</div>
  </div>
);
